/**
 * „E-Mail zuordnen" — Antoine, 09.09.2026: "Ich muss aber auch überall die Möglichkeit haben eine
 * E-Mail einem Vorgang zu ordnen zu können. Das fehlt mir noch diese komplette Dynamik." Bisher
 * ging Zuordnen nur vom Posteingang aus (Clearing-House-Ansicht). Dieser Drawer bringt denselben
 * Pool unzugeordneter Nachrichten (`GET /api/inbox`, Feld `unassigned` — Definition „event_id IS
 * NULL", s. `pages/posteingang.tsx`) direkt an einen Vorgang: Suche + Auswahl + sofortige
 * Erstzuordnung über `POST /api/messages/:id/assign` (Migration 321 — verlangt weiterhin eine
 * bislang unzugeordnete Nachricht, exakt das, was `unassigned` liefert).
 *
 * Bewusst ein EIGENER, schlanker Baustein statt einer Erweiterung von `ui/VorgangsPicker.tsx`
 * (der sucht Vorgänge, nicht Nachrichten) oder von `ZuordnungAendernModal` (der setzt eine BEREITS
 * bekannte Nachricht auf einen NEUEN Vorgang um — hier ist es umgekehrt: ein bekannter Vorgang
 * sucht sich eine bislang unzugeordnete Nachricht).
 */
import { useEffect, useMemo, useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, useToast } from "./components";
import { dekodiereMailBetreff, entferneMailClientArtefakte } from "../lib/mail-anzeige";

const CHANNEL_ICON: Record<"email" | "whatsapp", string> = { email: "mail", whatsapp: "chat" };

/** Eine Zeile aus `GET /api/inbox`s `unassigned`-Feld — nur die für die Auswahl nötigen Felder
 *  (dieselbe Route, dieselben Spalten wie `UnassignedGroup` in `pages/posteingang.tsx`, hier als
 *  eigene, schlankere Kopie statt eines Imports quer über eine Seiten-Datei). */
export interface UnassignedNachricht {
  id: string;
  channel: "email" | "whatsapp";
  fromAddr: string;
  subject: string | null;
  snippet: string;
  receivedAt: string;
  messageCount: number;
}

interface InboxUnassignedResponse {
  unassigned: UnassignedNachricht[];
}

function formatDatum(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export interface NachrichtAuswaehlenDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Der Vorgang, dem die ausgewählte Nachricht per `POST /api/messages/:id/assign` zugeordnet wird. */
  eventId: string;
  /** Nach erfolgreicher Zuordnung — Aufrufer schließt i.d.R. selbst nicht extra, der Drawer
   *  schließt sich bereits selbst (s. `waehlen` unten), reload bleibt Aufgabe des Aufrufers. */
  onZugeordnet: () => void;
}

export function NachrichtAuswaehlenDrawer({ open, onClose, eventId, onZugeordnet }: NachrichtAuswaehlenDrawerProps) {
  const { data, loading, error, reload } = useApi<InboxUnassignedResponse>(open ? "/api/inbox" : null);
  const [suche, setSuche] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setSuche("");
      setFehler(null);
      setBusyId(null);
    }
  }, [open]);

  const unassigned = data?.unassigned ?? [];
  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return unassigned;
    return unassigned.filter((m) => {
      const betreff = dekodiereMailBetreff(m.subject)?.toLowerCase() ?? "";
      return (
        m.fromAddr.toLowerCase().includes(q) ||
        betreff.includes(q) ||
        m.snippet.toLowerCase().includes(q)
      );
    });
  }, [unassigned, suche]);

  async function waehlen(m: UnassignedNachricht) {
    setBusyId(m.id);
    setFehler(null);
    try {
      await apiMutate(`/api/messages/${m.id}/assign`, "POST", { eventId });
      toast.success("Nachricht zugeordnet.");
      onZugeordnet();
      onClose();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Zuordnung fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="E-Mail zuordnen">
      <div className="flex h-full flex-col">
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-outline-variant/40 px-3 py-2 dark:border-outline/30">
          <Icon name="search" className="text-[16px] text-secondary dark:text-secondary-fixed-dim" />
          <input
            type="text"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Absender, Betreff oder Text durchsuchen…"
            aria-label="Unzugeordnete Nachrichten durchsuchen"
            className="min-w-0 flex-1 bg-transparent font-body-sm text-[13px] text-on-background outline-none placeholder:text-on-surface-variant/70 dark:text-inverse-on-surface"
          />
        </div>
        {fehler && <ErrorNote message={fehler} />}
        {loading ? (
          <LoadingRows rows={4} />
        ) : error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : unassigned.length === 0 ? (
          <EmptyState
            icon="mark_email_read"
            title="Keine unzugeordneten Nachrichten"
            hint="Im Clearing House liegt gerade keine E-Mail oder WhatsApp-Nachricht ohne Vorgang."
          />
        ) : treffer.length === 0 ? (
          <p className="px-1 py-6 text-center font-body-sm text-[12.5px] text-on-surface-variant/70">
            Keine Treffer für „{suche}“.
          </p>
        ) : (
          <ul className="flex-1 space-y-2 overflow-y-auto">
            {treffer.map((m) => {
              const betreff = dekodiereMailBetreff(m.subject)?.trim();
              const text = entferneMailClientArtefakte(m.snippet)?.trim();
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    disabled={busyId !== null}
                    onClick={() => void waehlen(m)}
                    className="flex w-full items-start gap-2.5 rounded-xl border border-outline-variant/40 px-3 py-2.5 text-left transition-colors hover:bg-surface-container-high disabled:opacity-60 dark:border-outline/30 dark:hover:bg-white/[0.04]"
                  >
                    <Icon name={CHANNEL_ICON[m.channel]} className="mt-0.5 shrink-0 text-[15px] text-secondary dark:text-secondary-fixed-dim" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate font-body-sm text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                          {m.fromAddr}
                        </span>
                        <span className="shrink-0 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
                          {formatDatum(m.receivedAt)}
                        </span>
                      </span>
                      {betreff && (
                        <span className="mt-0.5 block truncate font-body-sm text-[12.5px] font-medium text-on-background dark:text-inverse-on-surface">
                          {betreff}
                        </span>
                      )}
                      <span className="mt-0.5 line-clamp-1 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                        {text || "Ohne Text"}
                      </span>
                      {m.messageCount > 1 && (
                        <span className="mt-1 inline-block rounded-full bg-surface-container-high px-1.5 py-0.5 font-label-md text-[9.5px] text-secondary dark:bg-white/[0.06] dark:text-secondary-fixed-dim">
                          {m.messageCount} Nachrichten
                        </span>
                      )}
                    </span>
                    {busyId === m.id && <Icon name="progress_activity" className="mt-0.5 shrink-0 animate-spin text-[15px] text-secondary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Drawer>
  );
}
